import { useState } from 'react';
import { Button, Skeleton, Stack, Switch, Table, Text, TextInput } from '@mantine/core';
import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { Channel } from '@nelnela/contracts';
import { api, ApiError } from '../../lib/api';

type Row = Channel & { videoCount: number; addRequests: number };

export const CURATOR_CHANNELS_KEY = ['curator', 'channels'] as const;

/**
 * F15 curator → Channels (decision 3: most-added non-allowlisted channels first — the server
 * sorts). Plain table: name · external id · videos · add requests · allowlisted switch ·
 * notes (blur → PATCH). Rule 3: de-allowlisting flips that channel's shared videos back to
 * owner (a DB trigger) — the video list is invalidated too.
 */
export function CuratorChannels() {
  const qc = useQueryClient();
  const q = useInfiniteQuery({
    queryKey: CURATOR_CHANNELS_KEY,
    queryFn: ({ pageParam }) => api.curatorChannels({ cursor: pageParam }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
  const [line, setLine] = useState<string | null>(null);
  const patch = useMutation({
    mutationFn: (v: { id: string; allowlisted: boolean; notes?: string }) =>
      api.channelAllowlist(v.id, { allowlisted: v.allowlisted, notes: v.notes }),
    onSuccess: () => {
      setLine(null);
      void qc.invalidateQueries({ queryKey: CURATOR_CHANNELS_KEY });
      void qc.invalidateQueries({ queryKey: ['videos'] });
      void qc.invalidateQueries({ queryKey: ['curator', 'videos'] });
    },
    onError: (err) => {
      setLine(
        err instanceof ApiError
          ? (err.problem.detail ?? err.problem.title)
          : 'Couldn’t update that channel.',
      );
    },
  });
  const rows = q.data?.pages.flatMap((p) => p.items) ?? [];

  if (q.isPending) return <Skeleton height={120} radius="md" />;
  if (q.error) {
    return (
      <Text c="dimmed" fz="sm">
        {q.error instanceof ApiError && q.error.problem.status === 403
          ? 'Curator tools are for curators.'
          : 'Couldn’t load channels.'}
      </Text>
    );
  }
  return (
    <Stack gap="sm">
      <Table.ScrollContainer minWidth={720}>
        <Table striped highlightOnHover withTableBorder fz="sm">
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Channel</Table.Th>
              <Table.Th>External id</Table.Th>
              <Table.Th ta="right">Videos</Table.Th>
              <Table.Th ta="right">Add requests</Table.Th>
              <Table.Th>Allowlisted</Table.Th>
              <Table.Th>Notes</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {rows.length === 0 && (
              <Table.Tr>
                <Table.Td colSpan={6}>
                  <Text c="dimmed" fz="sm">
                    No channels yet — they appear as learners add videos.
                  </Text>
                </Table.Td>
              </Table.Tr>
            )}
            {rows.map((c) => (
              <ChannelRow
                key={c.id}
                c={c}
                busy={patch.isPending && patch.variables?.id === c.id}
                onPatch={(v) => patch.mutate({ id: c.id, ...v })}
              />
            ))}
          </Table.Tbody>
        </Table>
      </Table.ScrollContainer>
      {line && (
        <Text c="dimmed" fz="sm">
          {line}
        </Text>
      )}
      {q.hasNextPage && (
        <Button
          variant="default"
          size="xs"
          w="fit-content"
          onClick={() => q.fetchNextPage()}
          loading={q.isFetchingNextPage}
        >
          Load more
        </Button>
      )}
    </Stack>
  );
}

function ChannelRow({
  c,
  busy,
  onPatch,
}: {
  c: Row;
  busy: boolean;
  onPatch: (v: { allowlisted: boolean; notes?: string }) => void;
}) {
  // `Channel` on the wire has no `notes` — the field starts empty each load; a non-empty blur
  // sends the note along with the current allowlisted flag (the PATCH body needs both).
  const [notes, setNotes] = useState('');
  const [sent, setSent] = useState('');
  return (
    <Table.Tr>
      <Table.Td>
        <Text fz="sm" fw={500} lang="ka">
          {c.name ?? '—'}
        </Text>
      </Table.Td>
      <Table.Td>
        <Text ff="monospace" fz="xs" c="dimmed">
          {c.externalId}
        </Text>
      </Table.Td>
      <Table.Td ta="right" style={{ fontVariantNumeric: 'tabular-nums' }}>
        {c.videoCount}
      </Table.Td>
      <Table.Td ta="right" style={{ fontVariantNumeric: 'tabular-nums' }}>
        {c.addRequests}
      </Table.Td>
      <Table.Td>
        <Switch
          size="sm"
          checked={c.allowlisted}
          disabled={busy}
          onChange={(e) => onPatch({ allowlisted: e.currentTarget.checked })}
          aria-label={`Allowlist ${c.name ?? c.externalId}`}
        />
      </Table.Td>
      <Table.Td>
        <TextInput
          size="xs"
          placeholder="notes"
          value={notes}
          onChange={(e) => setNotes(e.currentTarget.value)}
          onBlur={() => {
            const v = notes.trim();
            if (v && v !== sent) {
              setSent(v);
              onPatch({ allowlisted: c.allowlisted, notes: v });
            }
          }}
          disabled={busy}
          w={220}
        />
      </Table.Td>
    </Table.Tr>
  );
}
